import type { DateTime } from 'luxon';

import Enums from '../types/api/Enums';
import { FormatTenantDisplayTimestamp } from './dateHelpers';
import { hasStringValue } from './equality';

export enum PriceEscalationStatus {
  Upcoming = 'Upcoming',
  Due = 'Due',
  Applied = 'Applied',
  NotApplicable = 'Not applicable',
}

export type PriceEscalationStatusSource = {
  escalationDate: DateTime;
  kind: Enums.EscalationKind;
  changeRatio: string | null;
  flatChangeAmount: string | null;
  isProcessed: boolean;
};

export const getPriceEscalationStatus = (
  escalation: PriceEscalationStatusSource,
  todayISO: string,
): PriceEscalationStatus => {
  if (escalation.isProcessed) {
    return PriceEscalationStatus.Applied;
  }

  if (!escalation.escalationDate.isValid) {
    return PriceEscalationStatus.NotApplicable;
  }

  const amount = Number(
    escalation.kind === Enums.EscalationKind.Flat
      ? escalation.flatChangeAmount
      : escalation.changeRatio,
  );
  if (!Number.isFinite(amount) || amount <= 0) {
    return PriceEscalationStatus.NotApplicable;
  }

  return (escalation.escalationDate.toISODate() ?? '') <= todayISO
    ? PriceEscalationStatus.Due
    : PriceEscalationStatus.Upcoming;
};

type AppliedDetails = { appliedAt: string | null; appliedByName: string | null };

export const formatAppliedDetails = ({ appliedAt, appliedByName }: AppliedDetails): string =>
  [FormatTenantDisplayTimestamp(appliedAt), appliedByName ?? ''].filter(hasStringValue).join(' · ');

export const formatPriceEscalationStatus = (
  status: PriceEscalationStatus,
  applied: AppliedDetails,
): string =>
  status === PriceEscalationStatus.Applied
    ? [status, formatAppliedDetails(applied)].filter(hasStringValue).join(' · ')
    : status;
